import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { get_lease_data_for_template } from '../../../../../utils/urls';
import { toastify } from '../../../../../utils/toast';
import { getItem } from '../../../../../utils/localStorage';

const PopulatedLeaseTemplate = ({ leaseId, visible, onHide }) => {
  const [loading, setLoading] = useState(true);
  const [populatedContent, setPopulatedContent] = useState('');

  const fetchAndPopulateTemplate = async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      
      if (!facilityId) {
        throw new Error('Facility ID is required');
      }
  
      // Use our specialized endpoint that returns all template data in one call
      const response = await makeRequest2(
        `${get_lease_data_for_template}/${facilityId}/${leaseId}`,
        'GET'
      );
  
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch lease data');
      }
  
      // Get the template data - handle nested data structure from API
      console.log('Template data:', response);
      
      const templateData = response.data.data || response.data;
      
      if (!templateData.templateContent) {
        throw new Error('No template content found');
      }
  
      // Start with the template content
      let content = templateData.templateContent;
      
      // Helper function to replace all occurrences of a placeholder
      const replaceAll = (str, placeholder, value) => {
        if (!value && value !== 0) value = 'N/A';
        
        // Escape special regex characters in the placeholder
        const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedPlaceholder, 'g');
        return str.replace(regex, value);
      };

      // Current date and other basics
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
      
      content = replaceAll(content, "{currentDate}", currentDate);
      content = replaceAll(content, "{documentTitle}", "LEASE AGREEMENT");
      content = replaceAll(content, "{propertyType}", templateData.unit?.unitType || "Residential Property");

      // 1. Tenant information
      Object.entries(templateData.tenant || {}).forEach(([key, value]) => {
        content = replaceAll(content, `{tenant.${key}}`, value);
      });
      
      // 2. Landlord information
      Object.entries(templateData.landlord || {}).forEach(([key, value]) => {
        content = replaceAll(content, `{landlord.${key}}`, value);
      });
      
      // 3. Unit information
      Object.entries(templateData.unit || {}).forEach(([key, value]) => {
        content = replaceAll(content, `{unit.${key}}`, value);
      });
      // Special case for lrNumber which might be landRateNumber in the data
      content = replaceAll(content, "{unit.lrNumber}", templateData.unit?.landRateNumber || "N/A");
      content = replaceAll(content, "{unit.address}", templateData.property?.address || "N/A");
      content = replaceAll(content, "{unit.description}", "Standard unit in good condition");

      // 4. Property information
      Object.entries(templateData.property || {}).forEach(([key, value]) => {
        content = replaceAll(content, `{property.${key}}`, value);
      });
      
      // 5. Currency information
      Object.entries(templateData.currency || {}).forEach(([key, value]) => {
        content = replaceAll(content, `{currency.${key}}`, value);
      });
      
      // 6. Lease status
      content = replaceAll(content, "{lease.status}", templateData.lease?.status || "N/A");
      
      // 7. Lease terms
      if (templateData.lease?.leaseTerms) {
        Object.entries(templateData.lease.leaseTerms).forEach(([key, value]) => {
          content = replaceAll(content, `{lease.leaseTerms.${key}}`, value);
        });
        
        // Special case for duration text
        const duration = templateData.lease.leaseTerms.duration || 0;
        const durationText = duration === 1 ? '1 month' : `${duration} months`;
        content = replaceAll(content, "{lease.leaseTerms.durationText}", durationText);
        
        // Special case for renewal notice period
        content = replaceAll(content, "{lease.leaseTerms.renewalNoticePeriod}", "2 months");
      }
      
      // 8. Financial terms
      if (templateData.lease?.financialTerms) {
        // Process standard financial terms
        const financialTerms = templateData.lease.financialTerms;
        
        // Monthly rent and security deposit
        content = replaceAll(content, "{lease.financialTerms.monthlyRent}", financialTerms.monthlyRent || 'N/A');
        content = replaceAll(content, "{lease.financialTerms.securityDeposit}", financialTerms.securityDeposit || 'N/A');
        
        // Deposit months - with fallback calculation if needed
        let depositMonths = financialTerms.depositMonths;
        
        // If deposit months is not available or is 'N/A' but we have rent and security deposit
        if ((!depositMonths || depositMonths === 'N/A') && 
            financialTerms.monthlyRent && 
            financialTerms.securityDeposit) {
          
          // Parse the values (remove commas if present)
          const monthlyRent = parseFloat(financialTerms.monthlyRent.replace(/,/g, ''));
          const securityDeposit = parseFloat(financialTerms.securityDeposit.replace(/,/g, ''));
          
          if (!isNaN(monthlyRent) && !isNaN(securityDeposit) && monthlyRent > 0) {
            // Calculate by dividing security deposit by monthly rent
            const calculatedMonths = Math.round(securityDeposit / monthlyRent);
            
            // Use the calculated value if it makes sense (between 1-12 months)
            if (calculatedMonths >= 1 && calculatedMonths <= 12) {
              depositMonths = calculatedMonths.toString();
              console.log(`Calculated deposit months: ${depositMonths}`);
            }
          }
        }
        
        // Use the calculated or original value for replacement
        content = replaceAll(content, "{lease.financialTerms.depositMonths}", depositMonths);
        
        // Other standard financial terms
        content = replaceAll(content, "{lease.financialTerms.balanceBroughtForward}", financialTerms.balanceBroughtForward || '0');
        content = replaceAll(content, "{lease.financialTerms.paymentDueDate}", financialTerms.paymentDueDate || 'N/A');
        
        // Special case for monthly rent in words
        content = replaceAll(content, "{lease.financialTerms.monthlyRentInWords}", 
          `${financialTerms.monthlyRent || 'N/A'} Only`);
        
        // Special case for annual increase percentage (might not exist)
        content = replaceAll(content, "{lease.financialTerms.annualIncreasePercentage}", "0");
        
        // Combined placeholders with currency
        content = replaceAll(content, 
          "{currency.currencyShortCode} {lease.financialTerms.monthlyRent}",
          `${templateData.currency.currencyShortCode} ${financialTerms.monthlyRent}`);
        
        content = replaceAll(content, 
          "{currency.currencyShortCode} {lease.financialTerms.securityDeposit}",
          `${templateData.currency.currencyShortCode} ${financialTerms.securityDeposit}`);
        
        // Service charge placeholders
        content = replaceAll(content, "{lease.financialTerms.serviceCharge}", "Included in rent");
        content = replaceAll(content, "{lease.financialTerms.serviceChargeItems}", "Water, Security, Garbage Collection");
      }

      // 9. Billing cycle
      if (templateData.lease?.billingCycle) {
        Object.entries(templateData.lease.billingCycle).forEach(([key, value]) => {
          content = replaceAll(content, `{lease.billingCycle.${key}}`, value);
        });
      }
      
      // 10. Payment methods
      if (templateData.lease?.financialTerms) {
        // All payment method types as a simple list
        const methodTypes = templateData.lease.financialTerms.paymentMethodsFormatted || 'N/A';
        content = replaceAll(content, "{lease.financialTerms.paymentMethodTypes}", methodTypes);
        
        // Primary payment method type
        const primaryMethodType = templateData.lease.financialTerms.primaryPaymentMethod?.type || 'N/A';
        content = replaceAll(content, "{lease.financialTerms.primaryPaymentMethod}", primaryMethodType);
        
        // Direct access to payment method details
        if (templateData.lease.financialTerms.primaryPaymentMethod?.details) {
          const details = templateData.lease.financialTerms.primaryPaymentMethod.details;
          
          // Bank details - directly access each field
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.bankName}", 
            details.bankName || 'N/A');
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.accountName}", 
            details.accountName || 'N/A');
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.accountNumber}", 
            details.accountNumber || 'N/A');
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.branch}", 
            details.branch || 'N/A');
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.swiftCode}", 
            details.swiftCode || 'N/A');
        } else {
          // Default values if no details available
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.bankName}", 'N/A');
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.accountName}", 'N/A');
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.accountNumber}", 'N/A');
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.branch}", 'N/A');
          content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].details.swiftCode}", 'N/A');
        }
        
        // Basic payment method info
        content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].type}", 
          templateData.lease.financialTerms.primaryPaymentMethod?.type || 'N/A');
        content = replaceAll(content, "{lease.financialTerms.paymentMethods[0].isPrimary ? 'Yes' : 'No'}", 
          templateData.lease.financialTerms.primaryPaymentMethod?.isPrimary || 'No');
          
        // All payment methods as simple list instead of formatted text
        content = replaceAll(content, "{lease.financialTerms.paymentMethods[*].type}", methodTypes);
      }
      
      // 11. M-Pesa details
      if (templateData.lease?.financialTerms) {
        content = replaceAll(content, "{lease.financialTerms.mpesaEnabled}", 
          templateData.lease.financialTerms.mpesaEnabled || 'No');
        
        if (templateData.lease.financialTerms.mpesaDetails) {
          Object.entries(templateData.lease.financialTerms.mpesaDetails).forEach(([key, value]) => {
            content = replaceAll(content, `{lease.financialTerms.mpesaDetails.${key}}`, value);
          });
        } else {
          content = replaceAll(content, "{lease.financialTerms.mpesaDetails.businessNumber}", "N/A");
          content = replaceAll(content, "{lease.financialTerms.mpesaDetails.accountNumber}", "N/A");
          content = replaceAll(content, "{lease.financialTerms.mpesaDetails.phoneNumber}", "N/A");
        }
      }
      
      // 12. Maintenance details (placeholders)
      content = replaceAll(content, "{maintenanceDetails.tenantResponsibilities}", 
        "Minor repairs, regular cleaning, reporting issues promptly");
      content = replaceAll(content, "{maintenanceDetails.landlordResponsibilities}", 
        "Structural repairs, major plumbing, electrical systems");
      content = replaceAll(content, "{maintenanceDetails.minorRepairLimit}", "5,000");
      content = replaceAll(content, "{maintenanceDetails.repairTimeline}", "48 hours for emergency, 7 days for non-emergency");
      
      // 13. Utilities (placeholders)
      content = replaceAll(content, "{utilities.electricity}", "Tenant responsibility");
      content = replaceAll(content, "{utilities.water}", "Included in rent");
      content = replaceAll(content, "{utilities.gas}", "Tenant responsibility if applicable");
      content = replaceAll(content, "{utilities.internet}", "Tenant responsibility");
      content = replaceAll(content, "{utilities.garbage}", "Included in service charge");
      
      // 14. Premises use (placeholders)
      content = replaceAll(content, "{premisesUse.permittedUse}", "Residential purposes only");
      content = replaceAll(content, "{premisesUse.occupancyLimit}", "Maximum 4 persons");
      content = replaceAll(content, "{premisesUse.prohibitedActivities}", 
        "Commercial activities, illegal activities, excessive noise, structural modifications without permission");
      
      // 15. Termination details
      content = replaceAll(content, "{terminationDetails.noticePeriod}", "2 months written notice");
      content = replaceAll(content, "{terminationDetails.breachNoticePeriod}", "14 days to remedy");
      
      // 16. Penalty information
      Object.entries(templateData.penalty || {}).forEach(([key, value]) => {
        content = replaceAll(content, `{penalty.${key}}`, value);
      });
      
      // 17. Reminder information
      Object.entries(templateData.reminder || {}).forEach(([key, value]) => {
        content = replaceAll(content, `{reminder.${key}}`, value);
      });
      
      // 18. Legal details (placeholders)
      content = replaceAll(content, "{legalDetails.governingLaw}", "Laws of Kenya");
      content = replaceAll(content, "{legalDetails.disputeResolution}", "Mediation, followed by arbitration");
      content = replaceAll(content, "{legalDetails.forceMajeure}", 
        "Neither party shall be liable for inability to perform obligations due to acts of God, civil unrest, war, governmental actions, or other events beyond reasonable control");
      
      // 19. Signatures
      content = replaceAll(content, "{signatories.landlordSignature}", "_______________________");
      content = replaceAll(content, "{signatories.tenantSignature}", "_______________________");
      content = replaceAll(content, "{signatories.landlordWitness}", "_______________________");
      content = replaceAll(content, "{signatories.tenantWitness}", "_______________________");
      content = replaceAll(content, "{signatures.tenant}", "_______________________");
      content = replaceAll(content, "{signatures.landlord}", "_______________________");
      content = replaceAll(content, "{signatures.tenantWitness}", "_______________________");
      content = replaceAll(content, "{signatures.landlordWitness}", "_______________________");
      
      // Set the content to display
      setPopulatedContent(content);
      setLoading(false);
    } catch (error) {
      console.error('Error in fetchAndPopulateTemplate:', error);
      toastify(error.message || 'Error loading template', 'error');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && leaseId) {
      fetchAndPopulateTemplate();
    } else {
      setPopulatedContent('');
      setLoading(true);
    }
  }, [visible, leaseId]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      toastify('Please allow pop-ups to print this document', 'error');
      return;
    }
    
    // Create print-friendly document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Lease Agreement</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 2cm;
              font-size: 12pt;
            }
            .header {
              text-align: center;
              margin-bottom: 2em;
            }
            .content {
              margin-bottom: 1em;
            }
            h1, h2, h3 {
              margin-top: 1.5em;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 1em 0;
            }
            td, th {
              border: 1px solid #ddd;
              padding: 8px;
            }
          </style>
        </head>
        <body>
          <div class="content">${populatedContent}</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Slight delay to ensure content is loaded
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownloadPDF = () => {
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      toastify('Please allow pop-ups to download this document', 'error');
      return;
    }
    
    // Create document for PDF conversion
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Lease Agreement</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 2cm;
              font-size: 12pt;
            }
            .content {
              margin-bottom: 1em;
            }
            h1, h2, h3 {
              margin-top: 1.5em;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 1em 0;
            }
            td, th {
              border: 1px solid #ddd;
              padding: 8px;
            }
            @media print {
              body {
                margin: 2cm;
              }
            }
          </style>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 500);
            };
          </script>
        </head>
        <body>
          <div class="content">${populatedContent}</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <Dialog
      header="Lease Agreement Document"
      visible={visible}
      style={{ width: '80vw', maxHeight: '90vh' }}
      onHide={onHide}
      className="populated-lease-template"
      maximizable
    >
      <div className="p-4">
        {loading ? (
          <div className="text-center py-4">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
            <div className="mt-2">Loading template...</div>
          </div>
        ) : (
          <div>
            <div className="mb-4 text-end">
              <Button
                icon="pi pi-download"
                label="Download PDF"
                className="p-button-secondary mr-2"
                onClick={handleDownloadPDF}
              />
              <Button
                icon="pi pi-print"
                label="Print"
                className="p-button-primary mr-2"
                onClick={handlePrint}
              />
              <Button
                icon="pi pi-times"
                label="Close"
                className="p-button-danger"
                onClick={onHide}
              />
            </div>
            <div className="template-content bg-white p-4 border rounded">
              <ReactQuill
                value={populatedContent}
                readOnly
                theme="bubble"
                modules={{ toolbar: false }}
              />
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        .populated-lease-template .p-dialog-content {
          overflow-y: auto;
        }

        .template-content {
          min-height: 50vh;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
        }

        .ql-container {
          font-size: 14px;
          line-height: 1.6;
        }

        .ql-editor {
          padding: 20px;
        }

        @media print {
          .p-dialog-header,
          .mb-4.text-end {
            display: none !important;
          }
          
          .p-dialog {
            box-shadow: none !important;
          }
          
          .template-content {
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
          }

          .ql-editor {
            padding: 0 !important;
          }

          .ql-container {
            font-size: 12pt;
          }
        }

        @page {
          margin: 2cm;
        }
      `}</style>
    </Dialog>
  );
};

export default PopulatedLeaseTemplate;